import { useState, useEffect, useCallback } from 'react';
import { auditAPI } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDateTime } from '@/lib/utils';
import { 
  ClipboardList, 
  Filter, 
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

export default function AuditLog() {
  const { t, language } = useLanguage();
  const [showFilters, setShowFilters] = useState(false);
  
  const ACTION_LABELS = {
    'user_created': t('audit.userCreated'),
    'user_updated': t('audit.userUpdated'),
    'user_deleted': t('audit.userDeleted'),
    'object_created': t('audit.objectCreated'),
    'object_updated': t('audit.objectUpdated'),
    'object_deleted': t('audit.objectDeleted'),
    'geofence_updated': t('audit.geofenceUpdated'),
    'rules_updated': t('audit.rulesUpdated'),
    'month_closed': t('audit.monthClosed'),
    'month_reopened': t('audit.monthReopened'),
    'role_changed': t('audit.roleChanged'),
    'absence_marked': t('audit.absenceMarked'),
  };
  
  const TARGET_LABELS = {
    'user': t('common.employee'),
    'object': t('common.object'),
    'rules': t('settings.title'),
    'month': t('months.title'),
    'absence': t('nav.absences'),
  };
  
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [actionFilter, setActionFilter] = useState('all');
  const [targetFilter, setTargetFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (actionFilter !== 'all') params.action = actionFilter;
      if (targetFilter !== 'all') params.target_type = targetFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const res = await auditAPI.list(params);
      setLogs(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      toast.error(t('common.error'));
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [actionFilter, targetFilter, startDate, endDate, t]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const getActionBadgeClass = (action) => {
    if (action.includes('deleted')) return 'bg-rose-500/20 text-rose-400 border-rose-500/20';
    if (action.includes('created')) return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/20';
    if (action.includes('updated') || action.includes('changed')) return 'bg-amber-500/20 text-amber-400 border-amber-500/20';
    if (action.includes('closed')) return 'bg-wp-medium/20 text-wp-light border-wp-medium/20';
    if (action.includes('reopened')) return 'bg-wp-light/20 text-wp-light border-wp-light/20';
    if (action.includes('marked')) return 'bg-orange-500/20 text-orange-400 border-orange-500/20';
    return 'bg-slate-500/20 text-wp-400 border-slate-500/20';
  };

  const activeFiltersCount = [actionFilter !== 'all', targetFilter !== 'all', startDate, endDate].filter(Boolean).length;

  return (
    <div className="space-y-6 md:space-y-8" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-2xl md:text-4xl font-bold text-white font-heading flex items-center gap-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-wp-medium/20 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 md:h-6 md:w-6 text-wp-light" />
            </div>
            {t('audit.title')}
          </p>
          <p className="text-wp-light/40 mt-1 text-sm md:text-base">{t('audit.subtitle')}</p>
        </div>
        <button 
          onClick={loadLogs} 
          disabled={loading}
          className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-wp-dark/30 hover:bg-wp-dark/50 text-wp-light/70 hover:text-wp-lightest rounded-xl border border-wp-dark/50 transition-colors disabled:opacity-50 text-sm"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh')}
        </button>
      </div>

      {/* Filters - collapsible on mobile */}
      <div className="glass-card rounded-2xl overflow-hidden">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full flex items-center justify-between p-4 md:hidden"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-wp-medium/20 flex items-center justify-center">
              <Filter className="h-4 w-4 text-wp-light" />
            </div>
            <span className="text-sm font-semibold text-white">{t('audit.filters')}</span>
            {activeFiltersCount > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-wp-medium/30 text-wp-lightest text-xs">{activeFiltersCount}</span>
            )}
          </div>
          {showFilters ? <ChevronUp className="h-4 w-4 text-wp-light/50" /> : <ChevronDown className="h-4 w-4 text-wp-light/50" />}
        </button>

        <div className={`p-4 md:p-6 ${showFilters ? 'block' : 'hidden md:block'}`}>
          <div className="hidden md:flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-wp-medium/20 flex items-center justify-center">
              <Filter className="h-5 w-5 text-wp-light" />
            </div>
            <p className="text-lg font-semibold text-white font-heading">{t('audit.filters')}</p>
          </div>
          
          <div className="grid grid-cols-2 md:flex md:flex-wrap gap-3 md:gap-4">
            <div className="space-y-1.5 col-span-2 md:col-span-1">
              <label className="text-xs md:text-sm font-medium text-wp-light/70">{t('audit.actionType')}</label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-full md:w-[200px] bg-wp-dark/30 border-wp-dark/50 text-white h-9 text-sm">
                  <SelectValue placeholder={t('audit.allActions')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('audit.allActions')}</SelectItem>
                  {Object.entries(ACTION_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 col-span-2 md:col-span-1">
              <label className="text-xs md:text-sm font-medium text-wp-light/70">{t('audit.targetType')}</label>
              <Select value={targetFilter} onValueChange={setTargetFilter}>
                <SelectTrigger className="w-full md:w-[200px] bg-wp-dark/30 border-wp-dark/50 text-white h-9 text-sm">
                  <SelectValue placeholder={t('audit.allTargets')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('audit.allTargets')}</SelectItem>
                  <SelectItem value="user">{TARGET_LABELS['user']}</SelectItem>
                  <SelectItem value="object">{TARGET_LABELS['object']}</SelectItem>
                  <SelectItem value="rules">{TARGET_LABELS['rules']}</SelectItem>
                  <SelectItem value="month">{TARGET_LABELS['month']}</SelectItem>
                  <SelectItem value="absence">{TARGET_LABELS['absence']}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs md:text-sm font-medium text-wp-light/70">{t('audit.from')}</label>
              <input
                type="date"
                lang={language}
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full h-9 px-3 rounded-xl bg-wp-dark/30 border border-wp-dark/50 text-white text-sm focus:outline-none focus:border-wp-light"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs md:text-sm font-medium text-wp-light/70">{t('audit.to')}</label>
              <input
                type="date"
                lang={language}
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-full h-9 px-3 rounded-xl bg-wp-dark/30 border border-wp-dark/50 text-white text-sm focus:outline-none focus:border-wp-light"
              />
            </div>

            {(startDate || endDate) && (
              <div className="col-span-2 md:col-span-1 flex items-end">
                <button
                  onClick={() => { setStartDate(''); setEndDate(''); }}
                  className="h-9 px-4 rounded-xl bg-white/5 hover:bg-white/10 text-white/50 hover:text-white text-sm transition-colors border border-white/10"
                >
                  {t('common.clear') || '✕'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop: Table / Mobile: Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      ) : logs.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center">
          <ClipboardList className="h-10 w-10 mx-auto text-wp-light/20 mb-3" />
          <p className="text-wp-light/40">{t('audit.noLogs')}</p>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {logs.map((log) => (
              <div key={log.id} className="glass-card rounded-xl p-4 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getActionBadgeClass(log.action)}`}>
                    {ACTION_LABELS[log.action] || log.action.replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs text-wp-light/40">{formatDateTime(log.created_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white">{log.user_name}</span>
                  <span className="text-xs px-2 py-0.5 bg-wp-dark/50 rounded text-wp-light/60">
                    {TARGET_LABELS[log.target_type] || log.target_type}
                  </span>
                </div>
                {log.details && (
                  <p className="text-xs text-wp-light/50 leading-relaxed">{log.details}</p>
                )}
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="glass-card rounded-2xl overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-wp-dark/50 hover:bg-transparent">
                    <TableHead className="text-wp-light/50">{t('audit.timestamp')}</TableHead>
                    <TableHead className="text-wp-light/50">{t('audit.user')}</TableHead>
                    <TableHead className="text-wp-light/50">{t('audit.action')}</TableHead>
                    <TableHead className="text-wp-light/50">{t('audit.target')}</TableHead>
                    <TableHead className="text-wp-light/50">{t('audit.details')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className="border-wp-dark/30 hover:bg-wp-dark/30" data-testid={`audit-row-${log.id}`}>
                      <TableCell className="text-wp-light/50 text-sm whitespace-nowrap">
                        {formatDateTime(log.created_at)}
                      </TableCell>
                      <TableCell className="font-medium text-white">{log.user_name}</TableCell>
                      <TableCell>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getActionBadgeClass(log.action)}`}>
                          {ACTION_LABELS[log.action] || log.action.replace(/_/g, ' ')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs px-2 py-0.5 bg-wp-dark/50 rounded text-wp-light/70">
                          {TARGET_LABELS[log.target_type] || log.target_type}
                        </span>
                        {log.details && (
                          <span className="ml-2 text-xs text-white/60 break-words">{log.details}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-wp-light/50 text-sm max-w-xs truncate">
                        {log.target_id ? log.target_id.substring(0, 8) + '...' : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
